import type { TStakingReward } from '@pages/vaults/components/widget/rewards/types'
import { hasRegisteredStakingSelector, type StakingFunctionSelector } from '@pages/vaults/domain/stakingRegistry'
import { JUICED_STAKING_REWARDS_ABI } from '@shared/contracts/abi/juicedStakingRewards.abi'
import { STAKING_REWARDS_ABI } from '@shared/contracts/abi/stakingRewards.abi'
import { V3_STAKING_REWARDS_ABI } from '@shared/contracts/abi/V3StakingRewards.abi'
import { VEYFI_GAUGE_ABI } from '@shared/contracts/abi/veYFIGauge.abi'
import { useReadContract } from '@shared/hooks/useAppWagmi'
import { toNormalizedValue } from '@shared/utils'
import { useCallback, useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import { resolveExecutionChainId } from '@/config/tenderly'

export type TRewardToken = {
  address: `0x${string}`
  symbol: string
  decimals: number
  price: number
  isFinished: boolean
}

type UseStakingRewardsParams = {
  stakingAddress?: `0x${string}`
  stakingSource?: string
  rewardTokens: TRewardToken[]
  userAddress?: `0x${string}`
  chainId: number
  enabled?: boolean
}

type UseStakingRewardsReturn = {
  rewards: TStakingReward[]
  isLoading: boolean
  refetch: () => void
}

function getStakingRewardsReadSelector(stakingSource?: string): StakingFunctionSelector {
  if (stakingSource === 'V3 Staking') return 'earnedMulti(address)'
  if (stakingSource === 'Juiced') return 'earned(address,address)'
  return 'earned(address)'
}

export function isStakingRewardsReadEnabled({
  stakingAddress,
  stakingSource,
  rewardTokens,
  userAddress,
  chainId,
  enabled = true
}: UseStakingRewardsParams): boolean {
  return Boolean(
    enabled &&
      stakingAddress &&
      userAddress &&
      rewardTokens.length > 0 &&
      hasRegisteredStakingSelector({
        chainId,
        stakingAddress,
        stakingSource,
        selector: getStakingRewardsReadSelector(stakingSource)
      })
  )
}

export function useStakingRewards(params: UseStakingRewardsParams): UseStakingRewardsReturn {
  const { stakingAddress, stakingSource, rewardTokens, userAddress, chainId, enabled = true } = params
  const executionChainId = resolveExecutionChainId(chainId)

  const isEnabled = isStakingRewardsReadEnabled({
    stakingAddress,
    stakingSource,
    rewardTokens,
    userAddress,
    chainId,
    enabled
  })

  // V3 staking uses earnedMulti to get all rewards in one call
  const isV3Staking = stakingSource === 'V3 Staking'
  const {
    data: v3EarnedMulti,
    isLoading: isLoadingV3,
    refetch: refetchV3
  } = useReadContract({
    address: stakingAddress,
    abi: V3_STAKING_REWARDS_ABI,
    functionName: 'earnedMulti',
    args: [userAddress!],
    chainId,
    query: { enabled: isEnabled && isV3Staking }
  })

  // veYFI gauge uses earned(account) for single dYFI reward
  const isVeYFIGauge = stakingSource === 'VeYFI'
  const {
    data: veYFIEarned,
    isLoading: isLoadingVeYFI,
    refetch: refetchVeYFI
  } = useReadContract({
    address: stakingAddress,
    abi: VEYFI_GAUGE_ABI,
    functionName: 'earned',
    args: [userAddress!],
    chainId,
    query: { enabled: isEnabled && isVeYFIGauge }
  })

  // Juiced uses earned(account, rewardsToken) for each token
  const isJuiced = stakingSource === 'Juiced'
  const juicedContracts = useMemo(() => {
    if (!isJuiced || !stakingAddress || !userAddress) return []
    return rewardTokens.map((token) => ({
      address: stakingAddress,
      abi: JUICED_STAKING_REWARDS_ABI,
      functionName: 'earned' as const,
      args: [userAddress, token.address] as const,
      chainId: executionChainId
    }))
  }, [executionChainId, isJuiced, stakingAddress, userAddress, rewardTokens])

  const {
    data: juicedEarned,
    isLoading: isLoadingJuiced,
    refetch: refetchJuiced
  } = useReadContracts({
    contracts: juicedContracts,
    query: { enabled: isEnabled && isJuiced && juicedContracts.length > 0 && !!executionChainId }
  })

  // Legacy/OP Boost staking uses earned(account) for single reward token
  const isLegacy =
    stakingSource === 'Legacy' || stakingSource === 'OP Boost' || (!isV3Staking && !isVeYFIGauge && !isJuiced)
  const {
    data: legacyEarned,
    isLoading: isLoadingLegacy,
    refetch: refetchLegacy
  } = useReadContract({
    address: stakingAddress,
    abi: STAKING_REWARDS_ABI,
    functionName: 'earned',
    args: [userAddress!],
    chainId,
    query: { enabled: isEnabled && isLegacy && rewardTokens.length === 1 }
  })

  const rewards = useMemo((): TStakingReward[] => {
    if (!isEnabled) return []

    if (isV3Staking && v3EarnedMulti) {
      return rewardTokens
        .map((token, index) => {
          const amount = v3EarnedMulti[index] ?? 0n
          const normalized = toNormalizedValue(amount, token.decimals)
          return {
            tokenAddress: token.address,
            symbol: token.symbol,
            decimals: token.decimals,
            amount,
            price: token.price,
            usdValue: normalized * token.price
          }
        })
        .filter((r) => r.amount > 0n)
    }

    if (isVeYFIGauge && veYFIEarned !== undefined && rewardTokens.length > 0) {
      const token = rewardTokens[0]
      const amount = veYFIEarned
      const normalized = toNormalizedValue(amount, token.decimals)
      if (amount > 0n) {
        return [
          {
            tokenAddress: token.address,
            symbol: token.symbol,
            decimals: token.decimals,
            amount,
            price: token.price,
            usdValue: normalized * token.price
          }
        ]
      }
      return []
    }

    if (isJuiced && juicedEarned) {
      return rewardTokens
        .map((token, index) => {
          const result = juicedEarned[index]
          const amount = result?.status === 'success' ? (result.result as bigint) : 0n
          const normalized = toNormalizedValue(amount, token.decimals)
          return {
            tokenAddress: token.address,
            symbol: token.symbol,
            decimals: token.decimals,
            amount,
            price: token.price,
            usdValue: normalized * token.price
          }
        })
        .filter((r) => r.amount > 0n)
    }

    if (isLegacy && legacyEarned !== undefined && rewardTokens.length > 0) {
      const token = rewardTokens[0]
      const amount = legacyEarned
      const normalized = toNormalizedValue(amount, token.decimals)
      if (amount > 0n) {
        return [
          {
            tokenAddress: token.address,
            symbol: token.symbol,
            decimals: token.decimals,
            amount,
            price: token.price,
            usdValue: normalized * token.price
          }
        ]
      }
      return []
    }

    return []
  }, [
    isEnabled,
    isV3Staking,
    isVeYFIGauge,
    isJuiced,
    isLegacy,
    v3EarnedMulti,
    veYFIEarned,
    juicedEarned,
    legacyEarned,
    rewardTokens
  ])

  const isLoading = isLoadingV3 || isLoadingVeYFI || isLoadingJuiced || isLoadingLegacy

  const refetch = useCallback(() => {
    if (isV3Staking) refetchV3()
    if (isVeYFIGauge) refetchVeYFI()
    if (isJuiced) refetchJuiced()
    if (isLegacy) refetchLegacy()
  }, [isV3Staking, isVeYFIGauge, isJuiced, isLegacy, refetchV3, refetchVeYFI, refetchJuiced, refetchLegacy])

  return { rewards, isLoading, refetch }
}
